import { useCallback, useLayoutEffect, useMemo, useRef, type MutableRefObject } from "react";
import type { OnAsyncFilterOptionsChunk } from "@/components/shared/asyncFilterAccordions/asyncFilterOptionChunk.types";
import {
  createEmptyIdLabelMapRecord,
  mergeIdLabelIntoMap,
  stableNamespaceFingerprint,
} from "@/utils/asyncFilterIdLabelMaps";

/**
 * Keeps **id → display label** maps in a ref, fed by async filter accordions via `onAsyncOptionsChunk`.
 * Use when the multiselect stores stable ids but your list API expects human-readable filter values.
 *
 * Pass a **stable** `namespaceKeys` array (e.g. module-level `const` or `useMemo`) so fingerprint does not
 * thrash; changing the set of keys resets all maps after layout.
 */
export function useAsyncFilterOptionLabelMaps(namespaceKeys: readonly string[]) {
  const fingerprint = stableNamespaceFingerprint(namespaceKeys);
  const mapsRef = useRef(createEmptyIdLabelMapRecord(namespaceKeys));

  useLayoutEffect(() => {
    mapsRef.current = createEmptyIdLabelMapRecord(namespaceKeys);
  }, [fingerprint, namespaceKeys]);

  const registerChunk = useMemo(() => {
    const byNamespace: Record<string, OnAsyncFilterOptionsChunk> = {};
    for (const ns of namespaceKeys) {
      const namespace = ns;
      byNamespace[namespace] = (items) => {
        const map = mapsRef.current[namespace];
        if (!map) return;
        for (const it of items) {
          mergeIdLabelIntoMap(map, it.id, it.name);
        }
      };
    }
    return (namespace: string): OnAsyncFilterOptionsChunk => {
      const fn = byNamespace[namespace];
      if (!fn) {
        throw new Error(`useAsyncFilterOptionLabelMaps: unknown namespace "${namespace}"`);
      }
      return fn;
    };
  }, [namespaceKeys]);

  const reset = useCallback(() => {
    mapsRef.current = createEmptyIdLabelMapRecord(namespaceKeys);
  }, [namespaceKeys]);

  return {
    /** Current id→label maps (read at apply-filter time; do not mutate from outside except via chunk/reset). */
    mapsRef: mapsRef as MutableRefObject<Record<string, Map<string, string>>>,
    /** Per-namespace handler for accordion `onAsyncOptionsChunk`. */
    registerChunk,
    /** Clear all maps (e.g. on “clear filters”). */
    reset,
  };
}
